import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { motion } from "motion/react";
import { useLanguage } from "../context/LanguageContext";
import { api } from "../utils/api";
import { useState } from "react";

export function ContactSection() {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    location: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      await api.submitContact(formData);
      setSubmitMessage(t('contact.success'));
      setFormData({
        name: '',
        phone: '',
        email: '',
        location: '',
        message: ''
      });
    } catch (error) {
      setSubmitMessage(t('contact.error'));
      console.error('Contact form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-32 px-6 lg:px-20 bg-gradient-to-b from-[#051F20] to-[#0B2B26] relative overflow-hidden">
      {/* Animated Background Pattern */}
      <motion.div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `radial-gradient(circle at 25px 25px, #8EB69B 2%, transparent 0%)`,
          backgroundSize: '50px 50px'
        }}
        animate={{
          backgroundPosition: ['0px 0px', '50px 50px']
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear"
        }}
      />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-block bg-white/10 backdrop-blur-xl px-6 py-3 rounded-full border border-[#8EB69B]/30 mb-6 shadow-2xl"
          >
            <p className="text-[#8EB69B] tracking-wider uppercase text-sm">{t('contact.title')}</p>
          </motion.div>
          <motion.h2 
            className="text-5xl lg:text-6xl text-white mb-6 leading-tight"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t('contact.title')}
          </motion.h2>
          <motion.p 
            className="text-white/90 text-lg max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t('contact.subtitle')}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div>
            <motion.div 
              className="bg-white/10 backdrop-blur-xl p-10 rounded-3xl border-2 border-white/20 shadow-2xl"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-3xl text-white mb-8">{t('contact.title')}</h3>
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-white/90 mb-2 block">{t('contact.form.name')}</label>
                    <Input 
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter your name here"
                      className="bg-white/5 border-2 border-[#8EB69B]/20 focus:border-[#8EB69B] rounded-xl h-14 px-6 text-white placeholder:text-white/50"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-white/90 mb-2 block">{t('contact.form.phone')}</label>
                    <Input 
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Enter your phone number"
                      className="bg-white/5 border-2 border-[#8EB69B]/20 focus:border-[#8EB69B] rounded-xl h-14 px-6 text-white placeholder:text-white/50"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-white/90 mb-2 block">{t('contact.form.email')}</label>
                  <Input 
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email address"
                    className="bg-white/5 border-2 border-[#8EB69B]/20 focus:border-[#8EB69B] rounded-xl h-14 px-6 text-white placeholder:text-white/50"
                    required
                  />
                </div>
                
                <div>
                  <label className="text-white/90 mb-2 block">{t('contact.form.location') !== 'contact.form.location' ? t('contact.form.location') : 'Farm Location'}</label>
                  <Input 
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder={t('contact.form.location') !== 'contact.form.location' ? t('contact.form.location') : 'Farm Location'}
                    className="bg-white/5 border-2 border-[#8EB69B]/20 focus:border-[#8EB69B] rounded-xl h-14 px-6 text-white placeholder:text-white/50"
                    required
                  />
                </div>
                
                <div>
                  <label className="text-white/90 mb-2 block">{t('contact.form.message')}</label>
                  <Textarea 
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Type your message here"
                    rows={6}
                    className="bg-white/5 border-2 border-[#8EB69B]/20 focus:border-[#8EB69B] rounded-xl px-6 py-4 resize-none text-white placeholder:text-white/50"
                    required
                  />
                </div>

                {submitMessage && (
                  <div className={`p-4 rounded-xl ${submitMessage.includes('error') ? 'bg-red-500/20 border border-red-500/50' : 'bg-green-500/20 border border-green-500/50'}`}>
                    <p className="text-white">{submitMessage}</p>
                  </div>
                )}
                
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-[#8EB69B] to-[#235347] hover:from-[#C9A961] hover:to-[#8EB69B] text-white py-7 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 rounded-2xl border-2 border-white/20 disabled:opacity-50"
                  >
                    {isSubmitting ? t('contact.form.submit') + '...' : t('contact.form.submit')}
                  </Button>
                </motion.div>
              </form>
            </motion.div>
          </div>

          {/* Contact Info Cards - 2x2 Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-5">
            <motion.div 
              className="bg-gradient-to-br from-[#235347] to-[#163832] p-8 rounded-2xl text-white shadow-xl border-2 border-[#8EB69B]/20 min-h-[170px]"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              whileHover={{ scale: 1.03, y: -5 }}
            >
              <div className="flex flex-col items-center text-center gap-4">
                <div className="h-16 w-16 bg-white/20 backdrop-blur-md rounded-xl flex items-center justify-center shadow-lg">
                  <Phone className="h-7 w-7 text-white" />
                </div>
                <div className="w-full max-w-[14rem] mx-auto text-center">
                  <h4 className="text-sm text-white/70 mb-2">{t('contact.callUs') || 'Call Us'}</h4>
                  <p className="text-white text-xl mb-1 w-full">8147851696</p>
                  <p className="text-white text-xl mb-1 w-full">8088841428</p>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="bg-white/10 backdrop-blur-xl p-8 rounded-2xl border-2 border-[#8EB69B]/20 shadow-xl min-h-[170px] overflow-hidden"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              whileHover={{ scale: 1.03, y: -5 }}
            >
              <div className="flex flex-col items-center text-center gap-4">
                <div className="h-16 w-16 bg-gradient-to-br from-[#8EB69B] to-[#235347] rounded-xl flex items-center justify-center shadow-lg">
                  <Mail className="h-7 w-7 text-white" />
                </div>
                <div className="w-full max-w-[14rem] mx-auto text-center">
                  <h4 className="text-sm text-white/70 mb-2">{t('contact.emailUs') || 'Email Us'}</h4>
                  <p className="text-white text-lg break-words w-full text-center">info@kalpakrushi.com</p>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="bg-white/10 backdrop-blur-xl p-8 rounded-2xl border-2 border-[#8EB69B]/20 shadow-xl min-h-[170px]"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              whileHover={{ scale: 1.03, y: -5 }}
            >
              <div className="flex flex-col items-center text-center gap-4">
                <div className="h-16 w-16 bg-gradient-to-br from-[#C9A961] to-[#8EB69B] rounded-xl flex items-center justify-center shadow-lg">
                  <MapPin className="h-7 w-7 text-white" />
                </div>
                <div className="w-full max-w-[14rem] mx-auto text-center">
                  <h4 className="text-sm text-white/70 mb-2">{t('contact.visitUs') || 'Visit Us'}</h4>
                  <p className="text-white text-lg w-full">Sharada Nagar</p>
                  <p className="text-white text-lg w-full">Tiptur, Karnataka</p>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="bg-gradient-to-br from-[#C9A961]/20 to-[#8EB69B]/20 backdrop-blur-xl p-8 rounded-2xl border-2 border-[#C9A961]/30 shadow-xl min-h-[170px]"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              whileHover={{ scale: 1.03, y: -5 }}
            >
              <div className="flex flex-col items-center text-center gap-4">
                <div className="h-16 w-16 bg-gradient-to-br from-[#235347] to-[#163832] rounded-xl flex items-center justify-center shadow-lg">
                  <Clock className="h-7 w-7 text-white" />
                </div>
                <div className="w-full max-w-[14rem] mx-auto text-center">
                  <h4 className="text-sm text-white/70 mb-2">{t('contact.businessHours') || 'Business Hours'}</h4>
                  <p className="text-white text-lg w-full">Mon - Sat</p>
                  <p className="text-white text-lg w-full">9 AM - 6 PM</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      <motion.div 
        className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#8EB69B]/10 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.1, 0.2, 0.1]
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div 
        className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#C9A961]/10 rounded-full blur-3xl"
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.2, 0.1, 0.2]
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
    </section>
  );
}